import React, { useState } from "react"
import {
  Modal,
  Typography,
  Button,
  Space,
  Alert,
  Row,
  Col,
  Checkbox,
  Divider,
  message,
  CopyOutlined,
  DownloadOutlined,
} from "@mimber/ui-web"
import type { RecoveryCodesDisplayProps } from "./RecoveryCodesDisplay.types"

const { Text, Paragraph } = Typography

export const RecoveryCodesDisplay: React.FunctionComponent<
  RecoveryCodesDisplayProps
> = ({ codes, onDone, ...modalProps }) => {
  const [hasSaved, setHasSaved] = useState(false)

  const handleCopyAll = async () => {
    const codesText = codes.join("\n")
    try {
      await navigator.clipboard.writeText(codesText)
      message.success("Recovery codes copied to clipboard")
    } catch {
      message.error("Failed to copy recovery codes. Please copy them manually.")
    }
  }

  const handleDownload = () => {
    const codesText = [
      "Mimber Recovery Codes",
      "Generated: " + new Date().toLocaleDateString(),
      "---",
      ...codes.map((code, i) => `${i + 1}. ${code}`),
      "---",
      "Keep these codes in a safe place.",
      "Each code can only be used once.",
    ].join("\n")

    const blob = new Blob([codesText], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "mimber-recovery-codes.txt"
    link.click()
    URL.revokeObjectURL(url)
    message.success("Recovery codes downloaded")
  }

  return (
    <Modal title="Recovery Codes" footer={null} width={520} {...modalProps}>
      {(close) => (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="warning"
            showIcon
            message="Save these codes somewhere safe"
            description="You won't be able to see these codes again. Each code can only be used once to sign in if you lose access to your MFA device."
          />

          <Row gutter={[16, 8]} data-testid="recovery-codes-grid">
            {codes.map((code, index) => (
              <Col span={12} key={index}>
                <Text code style={{ fontSize: 14 }}>
                  {index + 1}. {code}
                </Text>
              </Col>
            ))}
          </Row>

          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyAll}
              data-testid="copy-codes-button"
            >
              Copy All
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              data-testid="download-codes-button"
            >
              Download
            </Button>
          </Space>

          <Divider style={{ margin: "8px 0" }} />

          <Paragraph>
            <Checkbox
              checked={hasSaved}
              onChange={(e) => setHasSaved(e.target.checked)}
              data-testid="saved-checkbox"
            >
              I have saved my recovery codes
            </Checkbox>
          </Paragraph>

          <Button
            type="primary"
            block
            disabled={!hasSaved}
            onClick={() => {
              onDone?.()
              close()
            }}
            data-testid="done-button"
          >
            Done
          </Button>
        </Space>
      )}
    </Modal>
  )
}
